import { useRenderTracker } from '@app/shared/debug';
import { Seperator } from '@app/shared/components/Seperator';
import { Stop } from '@app/shared/models/tour.type';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_showModalContent, atom_modalType, atom_smallStopImageWidth } from '@app/shared/state/show-content-modal.atom';
import { atom_tourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_visitedLocations } from '@app/shared/state/visited.atom';
import { Box, Button, Container, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PlyrVideo } from '@app/shared/components/PlyrVideo';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper';
import { atom_savedStopState } from '@app/shared/state/saved-stop-state.atom';
import { RateUsModal } from './RateUsModal';
import { TriviaPage } from './TriviaPage';
import pako from 'pako';
import { getDeviceId } from '@app/shared/utils/device-id';
import { CouponPage, isCurrentlyWithinHours, formatHoursRange, useCountdownTimer } from '@app/features/map/coupon-page/CouponPage';
import { atom_scrollTarget, atom_scrollContainerRef } from '@app/shared/state/scroll-target.atom';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { passiveCollector, isWithinRadius } from '@app/shared/services/passive-data/passive-data-collector';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { mediaProgressStore } from '@app/shared/services/media-progress-store';

type StopDetailsProps = {
	children?: React.ReactNode;
	onContentChange?: (paddingTop: number) => void;
};

// Static styles — allocated once, shared across all renders
const STYLE_MEDIA_BLOCK = { marginTop: 20, marginBottom: 20 } as const;
const STYLE_IMG_FULL = { maxWidth: '100%', height: 'auto', display: 'block' } as const;
const STYLE_IMG_SMALL_STOP = { maxWidth: '100%', width: 'auto', height: 'auto', display: 'block', alignSelf: 'center' } as const;
const STYLE_AUDIO_FULL = { width: '100%' } as const;
const STYLE_WORD_BREAK = { wordBreak: 'break-word' as const };
const STYLE_BTN_BASE = { background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', gap: 6 } as const;
const STYLE_LIKE_BTN = { ...STYLE_BTN_BASE, paddingTop: 0 } as const;
const STYLE_DISLIKE_BTN = { ...STYLE_BTN_BASE, paddingBottom: 0 } as const;
const STYLE_FINISH_BTN = { width: '80%' } as const;
const STYLE_BOTTOM_PAD = { padding: '2.5%' } as const;

// Live countdown label for the coupon trigger button — isolated so the per-second tick
// only re-renders this small node, not the entire StopDetails tree.
const CouponTimerLabel: React.FC<{ claimedAt: string; onExpire: () => void }> = React.memo(({ claimedAt, onExpire }) => {
	const { timeLeft } = useCountdownTimer(claimedAt, onExpire);
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
			<i className="fa-light fa-hourglass" />
			{timeLeft || '12:00'}
		</Box>
	);
});

// Dynamic height carousel component for mid-page consecutive images
const DynamicImageCarousel: React.FC<{ images: any[]; startIndex: number; mainColor: string; isRTL?: boolean }> = ({ images, startIndex, mainColor, isRTL }) => {
	const [imageDimensions, setImageDimensions] = useState<{[key: number]: {width: number, height: number}}>({});
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const [containerWidth, setContainerWidth] = useState(0);
	const [carouselHeight, setCarouselHeight] = useState(400);
	const containerRef = useRef<HTMLDivElement>(null);

	// Preload images to get dimensions
	useEffect(() => {
		images.forEach((media: any, index: number) => {
			const img = new Image();
			const handleLoad = () => {
				setImageDimensions(prev => ({
					...prev,
					[index]: { width: img.naturalWidth, height: img.naturalHeight }
				}));
			};
			img.onload = handleLoad;
			img.src = media.url?.split('&token=')?.[0];
			// Handle cached images that are already loaded
			if (img.complete && img.naturalWidth > 0) {
				handleLoad();
			}
		});
	}, [images]);

	// Track container width
	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) {
				const width = containerRef.current.offsetWidth;
				if (width > 0) {
					setContainerWidth(width);
				}
			}
		};
		updateWidth();
		const timer = setTimeout(updateWidth, 100);
		window.addEventListener('resize', updateWidth);
		return () => {
			clearTimeout(timer);
			window.removeEventListener('resize', updateWidth);
		};
	}, []);

	// Update height when dimensions, slide, or width changes
	useEffect(() => {
		const dims = imageDimensions[currentSlideIndex];
		if (dims && containerWidth > 0) {
			const aspectRatio = dims.height / dims.width;
			const calculatedHeight = containerWidth * aspectRatio;
			const maxHeight = typeof window !== 'undefined' ? window.innerHeight * 0.7 : 600;
			const newHeight = Math.min(Math.max(calculatedHeight, 200), maxHeight);
			setCarouselHeight(newHeight);
		}
	}, [imageDimensions, currentSlideIndex, containerWidth]);

	return (
		<Box
			ref={containerRef}
			key={'carousel-group-' + startIndex}
			dir="ltr"
			sx={{
				width: '100%',
				maxWidth: '100%',
				height: carouselHeight,
				mt: '20px',
				mb: '20px',
				transition: 'height 0.65s cubic-bezier(0.25, 0.1, 0.25, 1)',
				overflow: 'hidden',
				position: 'relative',
				direction: 'ltr',
				'& .swiper': { width: '100%', height: '100%', position: 'relative' },
				'& .swiper-wrapper': { display: 'flex', height: '100%' },
				'& .swiper-slide': {
					flexShrink: 0, width: '100%', height: '100%',
					display: 'flex', alignItems: 'center', justifyContent: 'center'
				},
				'& .swiper-slide img': {
					maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto',
					objectFit: 'contain', userSelect: 'none', pointerEvents: 'none', WebkitUserDrag: 'none'
				},
				'& .swiper-button-next, & .swiper-button-prev': {
					position: 'absolute', top: '50%', transform: 'translateY(-50%)',
					width: '48px', height: '48px', zIndex: 10, cursor: 'pointer',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%',
					'&::after': {
						content: '""', width: '14px', height: '14px',
						borderRight: `3px solid ${mainColor}`, borderBottom: `3px solid ${mainColor}`,
					}
				},
				'& .swiper-button-prev': { left: '10px', '&::after': { transform: 'rotate(135deg)', marginLeft: '4px' } },
				'& .swiper-button-next': { right: '10px', '&::after': { transform: 'rotate(-45deg)', marginRight: '4px' } },
				'& .swiper-pagination': {
					position: 'absolute', bottom: '10px', left: '50%',
					transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 10
				},
				'& .swiper-pagination-bullet': {
					width: '10px', height: '10px', borderRadius: '50%',
					backgroundColor: 'white', opacity: 1, cursor: 'pointer'
				},
				'& .swiper-pagination-bullet-active': { backgroundColor: mainColor }
			}}
		>
			<Swiper
				modules={[Pagination, Navigation]}
				pagination={{ clickable: true }}
				navigation={true}
				spaceBetween={0}
				slidesPerView={1}
				dir="ltr"
				style={{ direction: 'ltr' }}
				onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex)}
			>
				{images.map((media, i) => (
					<SwiperSlide key={'carousel-img-' + startIndex + '-' + i}>
						<img src={media.url?.split('&token=')?.[0]} alt="" />
					</SwiperSlide>
				))}
			</Swiper>
			{images[currentSlideIndex]?.title && (
				<Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left', mt: 1, px: 2, color: 'text.secondary' }}>
					{images[currentSlideIndex].title}
				</Typography>
			)}
		</Box>
	);
};

export function decompress(gzippedContent: string) {
	const compressedData = Uint8Array.from(atob(gzippedContent), (c) =>
		c.charCodeAt(0)
	);
	const decompressedData = pako.ungzip(compressedData, { to: 'string' });
	return decompressedData;
}

export const StopDetails: React.FC<StopDetailsProps> = React.memo<StopDetailsProps>(
	function StopDetails({ onContentChange }: StopDetailsProps) {
		useRenderTracker('StopDetails');
		const { t, i18n } = useTranslation();
		const [state_activeStopState] = useRecoilState(atom_activeStopState);
		const tourState = useRecoilValue(atom_tourState);
		const setState_openedStopState = useSetRecoilState(atom_openedStopState);
		const setSavedStopState = useSetRecoilState(atom_savedStopState);
		const setState_showModal = useSetRecoilState(atom_showModalContent);
		const setState_modalType = useSetRecoilState(atom_modalType);
		const [state_visitedLocations, setState_visitedLocations] = useRecoilState(atom_visitedLocations);
		const [showRatingModal, setShowRatingModal] = useState(false);
		const [showCouponPage, setShowCouponPage] = useState(false);
		const [couponClaimedAt, setCouponClaimedAt] = useState<string | null>(null);
		const [state_scrollTarget, setState_scrollTarget] = useRecoilState(atom_scrollTarget);
		const scrollContainerFromRecoil = useRecoilValue(atom_scrollContainerRef);
		const isDesktop = useRecoilValue(atom_isDesktop);
		const setSmallStopImageWidth = useSetRecoilState(atom_smallStopImageWidth);
		const { fireTrigger } = useNotificationTrigger();
		const getGeolocation = useRecoilCallback(({ snapshot }) => () => {
			return snapshot.getLoadable(atom_geolocationState).getValue();
		});
		const maxScrollDepthRef = useRef(0);

		// Stop review (like/dislike) persisted in localStorage per device
		const REVIEWS_KEY = `stop_reviews_${getDeviceId()}`;
		const getStoredReviews = (): Record<string, 'like' | 'dislike'> => {
			try { return JSON.parse(localStorage.getItem(REVIEWS_KEY) || '{}'); } catch { return {}; }
		};
		const [stopReview, setStopReview] = useState<'like' | 'dislike' | null>(() => {
			if (!state_activeStopState?.id) return null;
			return getStoredReviews()[state_activeStopState.id] || null;
		});

		// Sync review state when active stop changes
		useEffect(() => {
			if (!state_activeStopState?.id) { setStopReview(null); return; }
			setStopReview(getStoredReviews()[state_activeStopState.id] || null);
		}, [state_activeStopState?.id]);

		const handleStopReview = (type: 'like' | 'dislike') => {
			if (!state_activeStopState?.id) return;
			const reviews = getStoredReviews();
			if (reviews[state_activeStopState.id] === type) {
				// Toggle off — user reverts their choice
				delete reviews[state_activeStopState.id];
				setStopReview(null);
			} else {
				reviews[state_activeStopState.id] = type;
				setStopReview(type);
			}
			localStorage.setItem(REVIEWS_KEY, JSON.stringify(reviews));
		};

		// Track max scroll depth (debounced)
		useEffect(() => {
			const container = scrollContainerFromRecoil;
			if (!container) return;

			// Reset on stop change
			maxScrollDepthRef.current = 0;
			let debounceTimer: ReturnType<typeof setTimeout> | null = null;

			const onScroll = () => {
				if (debounceTimer) clearTimeout(debounceTimer);
				debounceTimer = setTimeout(() => {
					const { scrollTop, scrollHeight, clientHeight } = container;
					if (scrollHeight <= clientHeight) return;
					const depth = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
					if (depth > maxScrollDepthRef.current) {
						maxScrollDepthRef.current = depth;
					}
				}, 300);
			};

			container.addEventListener('scroll', onScroll, { passive: true });
			return () => {
				container.removeEventListener('scroll', onScroll);
				if (debounceTimer) clearTimeout(debounceTimer);
			};
		}, [scrollContainerFromRecoil, state_activeStopState?.id]);

		// Send scroll depth on unmount (covers X button, back button, outside click)
		const activeStopRef = useRef(state_activeStopState);
		activeStopRef.current = state_activeStopState;
		useEffect(() => {
			return () => {
				if (activeStopRef.current && maxScrollDepthRef.current > 0) {
					passiveCollector.trackScrollDepth(activeStopRef.current as Stop, maxScrollDepthRef.current);
				}
			};
		}, []);

		// Handle scroll to target element
		useEffect(() => {
			if (state_scrollTarget) {
				// Add a small delay to ensure DOM is fully rendered after modal closes
				const timeoutId = setTimeout(() => {
					const titleTag = document.getElementById(state_scrollTarget);
					if (titleTag && scrollContainerFromRecoil) {
						// Scroll so the bottom of the target element (where the button is) aligns with the bottom of the screen + 80px extra
						const containerRect = scrollContainerFromRecoil.getBoundingClientRect();
						const targetRect = titleTag.getBoundingClientRect();
						const scrollTop = scrollContainerFromRecoil.scrollTop;
						const targetScrollPosition = targetRect.bottom - containerRect.top + scrollTop - containerRect.height + 80;
						// Scroll the container to the target position
						scrollContainerFromRecoil.scrollTo({
							top: targetScrollPosition,
							behavior: 'smooth'
						});

						setState_scrollTarget(null);
					} else if (titleTag) {
						// Fallback to default scrollIntoView if no container found
						titleTag.scrollIntoView({ behavior: 'smooth', block: 'center' });
						setState_scrollTarget(null);
					} else {
						console.warn('[StopDetails] Target element not found:', state_scrollTarget);
						setState_scrollTarget(null);
					}
				}, 300); // 300ms delay to ensure modal closes and content renders

				return () => clearTimeout(timeoutId);
			}
		}, [state_scrollTarget, scrollContainerFromRecoil]);		// Track external link clicks in stop details
		useEffect(() => {
			const handleLinkClick = (e: MouseEvent) => {
				const target = e.target as HTMLElement;
				const link = target.closest('a');

				if (link && link.href) {
					// Check if it's an external link (http/https)
					if (link.href.startsWith('http://') || link.href.startsWith('https://')) {
						// Track the external link click (passiveCollector fires Firebase events internally)
						if (state_activeStopState) {
							passiveCollector.trackExternalLink(state_activeStopState as Stop, link.href);
						}
					}
				}
			};
			// Add click listener to the entire stop details container
			document.addEventListener('click', handleLinkClick, true); // Use capture phase to catch before other handlers

			// Cleanup
			return () => {
				document.removeEventListener('click', handleLinkClick, true);
			};
		}, [state_activeStopState]);

		function getHighestStopIndex() {

			const highest = tourState?.stops?.reduce((acc: number, next: Stop) => {
				if (next.stop_index && next.stop_index > acc) {
					return next.stop_index;
				}
				return acc;
			}, -1);
			return highest;
		}

		// Shared render functions for each media type
		const renderVideo = (media: any, i: number, showSeparator: boolean, opts?: { noPadding?: boolean; hideTitle?: boolean }) => (
			<div key={'video-' + media.order + '-' + i} className="fragment" style={STYLE_MEDIA_BLOCK}>
				<Box>
					{media.title && !opts?.hideTitle && (
						<Typography className={opts?.noPadding ? undefined : 'p-4 pb-2'} sx={{ lineHeight: isDesktop ? 1.8 : undefined, fontSize: isDesktop ? '1.125rem' : undefined }}>
							{media.title}
						</Typography>
					)}
					<PlyrVideo
						key={'plyr' + media.order}
						url={media.url?.split('&token=')?.[0] || ''}
						progressKey={state_activeStopState?.id}
						isVertical={media?.isVertical === true || (media?.isVertical as unknown) === 'true'}
					/>
				</Box>
				{showSeparator && <Seperator />}
			</div>
		);

		const renderAudio = (media: any, i: number, showSeparator: boolean, opts?: { noPadding?: boolean; hideTitle?: boolean }) => (
			<div key={'audio-' + media.order + '-' + i} className="fragment" style={STYLE_MEDIA_BLOCK}>
				<Box>
					{media.title && !opts?.hideTitle && (
						<Typography className={opts?.noPadding ? undefined : 'p-4 pb-2'} sx={{ lineHeight: isDesktop ? 1.8 : undefined, fontSize: isDesktop ? '1.125rem' : undefined }}>
							{media.title}
						</Typography>
					)}
					<audio
						controls
						className="h-7 mt-1 mb-2"
						style={STYLE_AUDIO_FULL}
						ref={(el) => {
							if (!el) return;
							if (el.dataset.tracked) return;
							el.dataset.tracked = 'true';
							const stopId = state_activeStopState?.id;
							const handleLoaded = () => {
								if (stopId) {
									const saved = mediaProgressStore.getAudio(stopId);
									if (saved > 0) el.currentTime = saved;
								}
							};
							const trackAudio = () => {
								if (stopId && el.currentTime > 0) {
									mediaProgressStore.setAudio(stopId, el.currentTime);
								}
								if (el.duration && state_activeStopState && stopId) {
									const percent = mediaProgressStore.getAudioPercent(stopId, el.duration);
									passiveCollector.trackAudioPlay(state_activeStopState as Stop, percent);
								}
							};
							const handleEnded = () => {
								if (state_activeStopState && stopId) {
									passiveCollector.trackAudioPlay(state_activeStopState as Stop, 100);
								}
								if (stopId) mediaProgressStore.setAudio(stopId, 0);
							};
							el.addEventListener('loadedmetadata', handleLoaded);
							el.addEventListener('pause', trackAudio);
							el.addEventListener('ended', handleEnded);
						}}
					>
						<source
							src={media.url?.split('&token=')?.[0]}
							type="audio/mpeg"
						/>
						Your browser does not support the audio element.
					</audio>
				</Box>
				{showSeparator && <Seperator />}
			</div>
		);

		const renderQuestion = (media: any, i: number, showSeparator: boolean) => (
			<div key={'question-' + media.order + '-' + i} className="fragment">
				<TriviaPage question={media} />
				{showSeparator && <Seperator />}
			</div>
		);

		const renderImage = (media: any, i: number, showSeparator: boolean, opts?: { noPadding?: boolean; hideTitle?: boolean }) => {
			return (
			<Container key={'image-' + media.order + '-' + i} disableGutters={!!opts?.noPadding} sx={{ mt: '20px', mb: '20px' }}>
				<Box sx={{ width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
					<img style={STYLE_IMG_FULL} src={media.url?.split('&token=')?.[0]} />
					{media.title && (
						<Typography variant="body2" sx={{ textAlign: (i18n as any).language === 'he' ? 'right' : 'left', mt: 1, color: 'text.secondary' }}>
							{media.title}
						</Typography>
					)}
				</Box>
				{showSeparator && <Seperator />}
			</Container>
		);
		};

		const renderText = (media: any, i: number, showSeparator: boolean) => (
			<div key={'text-' + media.order + '-' + i} className="fragment">
				<Box className="p-4 pb-2">
					{media.title && <Typography sx={{ lineHeight: isDesktop ? 1.8 : undefined, fontSize: isDesktop ? '1.125rem' : undefined }}>{media.title}</Typography>}
					<Typography
						style={STYLE_WORD_BREAK}
						variant="body2"
						sx={{ lineHeight: isDesktop ? 1.8 : undefined, fontSize: isDesktop ? '0.975rem' : undefined }}
					>
						{media.content}
					</Typography>
				</Box>
				{showSeparator && <Seperator />}
			</div>
		);

		const renderHtml = (media: any, i: number, showSeparator: boolean) => {
			let htmlContent = '';
			try {
				htmlContent = decompress(media?.content as string);
			} catch (err) {
				htmlContent = media?.content as string;
			}

			// Only modify bottom positioning on mobile devices (skip on desktop)
			if (!isDesktop && (htmlContent.includes('position: sticky') || htmlContent.includes('position:sticky'))) {
				// Use pure CSS - browser handles it natively with no JavaScript lag
				// Position button above the 60px mobile bottom bar plus safe-area padding
				htmlContent = htmlContent.replace(
					/bottom:\s*[\d.]+px/g,
					'bottom: calc(60px + env(safe-area-inset-bottom, 0px))'
				);
			}

			return (
				<Box className="p-4 pb-2 ml-2" key={'rhtml-' + (media.order ?? i) + '-' + i}>
					<div
						id={`media-title-${media.name}`}
						dangerouslySetInnerHTML={{ __html: htmlContent }}
					></div>
					{showSeparator && <Seperator />}
				</Box>
			);
		};

		const couponMedia = state_activeStopState?.all_media?.find((m: any) => m.type?.includes('coupon'));
		const renderCouponTrigger = (media: any, i: number, showSeparator: boolean) => {
			const withinHours = isCurrentlyWithinHours(media.availableHours);
			const hoursRangeDisplay = formatHoursRange(media.availableHours);
			const openingTime = media.availableHours?.start || '';
			const hasRichData = !!(media.product_name || media.product_pic || media.offer_in_details);

			// Fallback: legacy coupons without rich fields — keep the original single button
			if (!hasRichData) {
				return (
					<Box className="p-4 pb-2" key={'coupon-trigger-' + media.order + '-' + i} sx={{ textAlign: 'center' }}>
						<Button
							fullWidth
							onClick={() => setShowCouponPage(true)}
							sx={{
								bgcolor: '#C4702B',
								color: 'white',
								borderRadius: '12px',
								py: 1.5,
								fontWeight: 600,
								textTransform: 'none',
								fontSize: '1rem',
								'&:hover': { bgcolor: '#8B4513' },
							}}
						>
							{couponClaimedAt
								? <CouponTimerLabel claimedAt={couponClaimedAt} onExpire={() => setCouponClaimedAt(null)} />
								: (t('on_tour.stop.click_to_redeem_coupon') || 'Click to redeem coupon')}
						</Button>
						{showSeparator && <Seperator />}
					</Box>
				);
			}

			return (
				<Box
					key={'coupon-trigger-' + media.order + '-' + i}
					sx={{
						mx: 2,
						mb: 2,
						bgcolor: '#FFF8F2',
						borderRadius: '16px',
						border: '1px solid #E8DDD4',
						overflow: 'hidden',
					}}
				>
					{media.product_pic && (
						<Box sx={{ width: '100%', aspectRatio: '2.4 / 1', bgcolor: '#E8C9A0', overflow: 'hidden' }}>
							<img
								src={media.product_pic}
								alt={media.product_name || ''}
								style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
							/>
						</Box>
					)}
					<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
						{media.product_name && (
							<Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#3D2B1F' }}>
								{media.product_name}
							</Typography>
						)}
						{media.offer_in_details && (
							<Typography sx={{ fontSize: '0.875rem', color: '#7A6259', lineHeight: 1.5, wordBreak: 'break-word' }}>
								{media.offer_in_details}
							</Typography>
						)}
						{hoursRangeDisplay && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
								<i className="fa-light fa-clock" style={{ color: '#9C7B6E', fontSize: '0.85rem' }} />
								<Typography sx={{ fontSize: '0.8rem', color: '#9C7B6E' }}>
									{withinHours
										? `${t('on_tour.stop.active') || 'Available'} ${hoursRangeDisplay}`
										: `${t('on_tour.stop.not_available_yet') || 'Not available yet'} · ${t('on_tour.stop.opens_at') || 'Opens at'} ${openingTime}`
									}
								</Typography>
							</Box>
						)}
						<Button
							fullWidth
							disabled={!withinHours && !couponClaimedAt}
							onClick={() => setShowCouponPage(true)}
							sx={{
								mt: 1,
								bgcolor: (withinHours || couponClaimedAt) ? '#C4702B' : '#D4BBA8',
								color: 'white',
								borderRadius: '22px',
								py: 1.25,
								fontWeight: 700,
								textTransform: 'none',
								fontSize: '0.95rem',
								'&:hover': { bgcolor: (withinHours || couponClaimedAt) ? '#8B4513' : '#D4BBA8' },
								'&.Mui-disabled': { color: '#FFF8F2', bgcolor: '#D4BBA8' },
							}}
						>
							{couponClaimedAt
								? <CouponTimerLabel claimedAt={couponClaimedAt} onExpire={() => setCouponClaimedAt(null)} />
								: (t('on_tour.stop.reveal_coupon') || 'Reveal coupon')}
						</Button>
					</Box>
					{showSeparator && <Seperator />}
				</Box>
			);
		};

		// Main render function that routes to the correct media renderer
		const renderMediaItem = (media: any, i: number, totalLength: number, opts?: { noPadding?: boolean; hideTitle?: boolean; nextMedia?: any }) => {
			const isMedia = media.type?.includes('image') || media.type?.includes('video') || media.type?.includes('audio');
			const nextIsMedia = opts?.nextMedia?.type?.includes('image') || opts?.nextMedia?.type?.includes('video') || opts?.nextMedia?.type?.includes('audio');
			const showSeparator = i < totalLength - 1 && !isMedia && !nextIsMedia;

			if (media.type?.includes('video')) return renderVideo(media, i, showSeparator, opts);
			if (media.type?.includes('audio')) return renderAudio(media, i, showSeparator, opts);
			if (media.type?.includes('question')) return renderQuestion(media, i, showSeparator);
			if (media.type?.includes('image')) return renderImage(media, i, showSeparator, opts);
			if (media.type?.includes('text')) return renderText(media, i, showSeparator);
			if (media.type?.includes('html')) return renderHtml(media, i, showSeparator);
			if (media.type?.includes('coupon')) return renderCouponTrigger(media, i, showSeparator);
			return null;
		};

		// Check if this is a small stop
		const isSmallStop = state_activeStopState?.type?.toLowerCase() === 'smallstop';

		// For small stops: separate file media (video/audio/image) from text media (html/text)
		const fileMedia = state_activeStopState?.all_media.filter(media =>
			media.type?.includes('video') || media.type?.includes('audio') || media.type?.includes('image')
		) || [];

		// For small stops: image/video files for carousel
		const imageVideoMedia = state_activeStopState?.all_media.filter(media =>
			media.type?.includes('video') || media.type?.includes('image')
		) || [];

		const textMedia = state_activeStopState?.all_media.filter(media =>
			media.type?.includes('text') || media.type?.includes('html')
		) || [];

		// Group consecutive images for inline carousels
		const getGroupedMedia = () => {
			const allMedia = state_activeStopState?.all_media || [];
			const grouped: { type: 'single' | 'carousel'; items: any[]; startIndex: number }[] = [];
			let i = 0;

			while (i < allMedia.length) {
				const current = allMedia[i];
				if (current.type?.includes('image')) {
					// Collect consecutive images
					const imageGroup: any[] = [current];
					let j = i + 1;
					while (j < allMedia.length && allMedia[j].type?.includes('image')) {
						imageGroup.push(allMedia[j]);
						j++;
					}
					if (imageGroup.length >= 2) {
						grouped.push({ type: 'carousel', items: imageGroup, startIndex: i });
					} else {
						grouped.push({ type: 'single', items: [current], startIndex: i });
					}
					i = j;
				} else {
					grouped.push({ type: 'single', items: [current], startIndex: i });
					i++;
				}
			}
			return grouped;
		};

		// Render a carousel for consecutive images (uses dynamic height component)
		const renderConsecutiveImageCarousel = (images: any[], startIndex: number) => {
			const mainColor = tourState?.config?.maincolor ? `#${tourState.config.maincolor.replace('#', '')}` : '#c41c1c';
			return <DynamicImageCarousel key={'carousel-group-' + startIndex} images={images} startIndex={startIndex} mainColor={mainColor} isRTL={(i18n as any).language === 'he'} />;
		};

		// Render grouped media (handles consecutive image carousels)
		const renderGroupedMedia = () => {
			const grouped = getGroupedMedia();
			const totalItems = state_activeStopState?.all_media?.length ?? 0;
			const allMedia = state_activeStopState?.all_media || [];

			return grouped.map((group, groupIndex) => {
				if (group.type === 'carousel') {
					return renderConsecutiveImageCarousel(group.items, group.startIndex);
				} else {
					const media = group.items[0];
					// Find the next media item (from next group) to suppress separator before image/video
					const nextGroup = grouped[groupIndex + 1];
					const nextMedia = nextGroup ? (nextGroup.type === 'carousel' ? nextGroup.items[0] : nextGroup.items[0]) : undefined;
					return renderMediaItem(media, group.startIndex, totalItems, { nextMedia });
				}
			});
		};

		// Render small stop image/video carousel (when 2+ image/video files)
		const renderSmallStopCarousel = () => {
			if (imageVideoMedia.length <= 1) return null;

			const mainColor = tourState?.config?.maincolor ? `#${tourState.config.maincolor.replace('#', '')}` : '#c41c1c';
			return (
				<Box dir="ltr" sx={{
					width: '100%',
					maxWidth: '100%',
					height: { xs: '400px', md: '500px' },
					overflow: 'hidden',
					position: 'relative',
					direction: 'ltr',
					'& .swiper': {
						width: '100%',
						height: '100%',
						position: 'relative'
					},
					'& .swiper-wrapper': {
						display: 'flex',
						height: '100%'
					},
					'& .swiper-slide': {
						flexShrink: 0,
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					},
					'& .swiper-slide img, & .swiper-slide video': {
						width: '100%',
						height: '100%',
						objectFit: 'contain',
						objectPosition: 'center',
						backgroundColor: 'white',
						userSelect: 'none',
						pointerEvents: 'none',
						WebkitUserDrag: 'none'
					},
					// Re-enable pointer events on Plyr's video so the controls stay interactive
					// (the broader rule above disables pointerEvents to stop image drags).
					'& .swiper-slide .plyr video': {
						pointerEvents: 'auto',
					},
					'& .swiper-button-next, & .swiper-button-prev': {
						position: 'absolute',
						top: '50%',
						transform: 'translateY(-50%)',
						width: '48px',
						height: '48px',
						zIndex: 10,
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255,255,255,0.8)',
						borderRadius: '50%',
						'&::after': {
							content: '""',
							width: '14px',
							height: '14px',
							borderRight: `3px solid ${mainColor}`,
							borderBottom: `3px solid ${mainColor}`,
						}
					},
					'& .swiper-button-prev': {
						left: '10px',
						'&::after': {
							transform: 'rotate(135deg)',
							marginLeft: '4px'
						}
					},
					'& .swiper-button-next': {
						right: '10px',
						'&::after': {
							transform: 'rotate(-45deg)',
							marginRight: '4px'
						}
					},
					'& .swiper-pagination': {
						position: 'absolute',
						bottom: '10px',
						left: '50%',
						transform: 'translateX(-50%)',
						display: 'flex',
						gap: '8px',
						zIndex: 10
					},
					'& .swiper-pagination-bullet': {
						width: '10px',
						height: '10px',
						borderRadius: '50%',
						backgroundColor: 'white',
						opacity: 1,
						cursor: 'pointer'
					},
					'& .swiper-pagination-bullet-active': {
						backgroundColor: mainColor
					}
				}}>
					<Swiper
						modules={[Pagination, Navigation]}
						pagination={{ clickable: true }}
						navigation={true}
						spaceBetween={0}
						slidesPerView={1}
						dir="ltr"
						style={{ direction: 'ltr' }}
					>
						{imageVideoMedia.map((media, i) => (
							<SwiperSlide key={'small-carousel-' + i}>
								{media.type?.includes('video') ? (
									<PlyrVideo
										url={media.url?.split('&token=')?.[0] || ''}
										progressKey={state_activeStopState?.id}
										isVertical={media?.isVertical === true || (media?.isVertical as unknown) === 'true'}
									/>
								) : (
									<img
										src={media.url?.split('&token=')?.[0]}
										alt=""
									/>
								)}
							</SwiperSlide>
						))}
					</Swiper>
				</Box>
			);
		};

		// Reset small-stop image width when stop changes or unmounts
		useEffect(() => {
			setSmallStopImageWidth(null);
			return () => setSmallStopImageWidth(null);
		}, [state_activeStopState?.id]);

		// Determine padding based on first content type
		useEffect(() => {
			if (onContentChange) {
				// For small stops: if file media exists (image/video/audio), no padding needed
				// For big stops: check the first media item type
				if (isSmallStop) {
					// If there's image/video media at the top, don't add padding
					const paddingTop = imageVideoMedia.length > 0 ? 0 : 40;
					onContentChange(paddingTop);
				} else {
					// For big stops, check if first media is HTML or text
					const firstMedia = state_activeStopState?.all_media?.[0];
					const isHtmlOrText = firstMedia?.type === 'text/html' || firstMedia?.type === 'text/plain';
					const paddingTop = isHtmlOrText ? 0 : 20;
					onContentChange(paddingTop);
				}
			}
		}, [isSmallStop, imageVideoMedia.length, state_activeStopState?.all_media?.[0]?.type, onContentChange]);

		// Collect all buttons from HTML media items (concise + TS-safe)
		const allButtons: { link: string; text: string; color: string }[] = (state_activeStopState?.all_media ?? [])
			.filter((m: any) =>
				typeof m?.type === 'string' &&
				m.type.includes('html') &&
				typeof m?.affiliate_link === 'string' &&
				typeof m?.but_text === 'string'
			)
			.map((m: any) => ({
				link: m.affiliate_link as string,
				text: m.but_text as string,
				color:
					typeof m?.color === 'string' && m.color
						? `#${(m.color as string).replace('#', '')}`
						: '#c41c1c',
			}));

		// Only show the first button if there are multiple
		const buttonToShow = allButtons.length > 0 ? allButtons[0] : null;

		return (
			<>
			{showCouponPage && couponMedia && ReactDOM.createPortal(
				<Box sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					width: '100vw',
					maxHeight: '80dvh',
					bgcolor: '#FAF6F1',
					zIndex: 2000,
					overflowY: 'auto',
					pb: '70px',
					borderTopLeftRadius: '20px',
					borderTopRightRadius: '20px',
					boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
				}}>
					<CouponPage
						media={couponMedia}
						onClose={() => setShowCouponPage(false)}
						onClaimUpdate={setCouponClaimedAt}
					/>
				</Box>,
				document.body
			)}
			<Box className="flex flex-col gap-2" sx={{ position: 'relative', height: 'auto' }}>
				{isSmallStop ? (
					// Small stop: carousel if 2+ image/video, otherwise single image/video. Audio renders in order.
					<>
						{/* Render carousel if 2+ image/video files */}
						{imageVideoMedia.length >= 2 ? (
							renderSmallStopCarousel()
						) : imageVideoMedia.length === 1 ? (
							/* Render single image/video at top — image dictates popup width */
							imageVideoMedia[0].type?.includes('image') ? (
								<img
									src={imageVideoMedia[0].url?.split('&token=')?.[0]}
									alt=""
									onLoad={(e) => {
										const img = e.currentTarget;
										setSmallStopImageWidth(img.naturalWidth);
									}}
									style={STYLE_IMG_SMALL_STOP}
								/>
							) : (
								<Box sx={{ maxWidth: 550, width: '100%', mx: 'auto' }}>
									{renderMediaItem(imageVideoMedia[0], 0, 1, { noPadding: true, hideTitle: true })}
								</Box>
							)
						) : null}
						{/* Render stop name after image/video media */}
						{imageVideoMedia.length > 0 && state_activeStopState?.s_title && (
							<Box>
								<Typography
									variant="h4"
									style={{
										fontWeight: '500',
										textAlign: ((i18n as any).language === 'he') ? 'right' : 'left',
										paddingLeft: 16,
										paddingRight: 16
									}}
								>
									{state_activeStopState.s_title}
								</Typography>
							</Box>
						)}

						{/* Render audio and text media in order */}
						{state_activeStopState?.all_media?.filter(media =>
							media.type?.includes('audio') || media.type?.includes('text') || media.type?.includes('html')
						).map((media, i, arr) => renderMediaItem(media, i, arr.length))}
					</>
				) : (
					// Big stop: render all media with consecutive images merged into carousels
					<>
						{renderGroupedMedia()}
					</>
				)}
				<div style={STYLE_BOTTOM_PAD}></div>
				<RateUsModal
					showModal={showRatingModal}
					onClose={() => {
						setShowRatingModal(false);
						setState_openedStopState(undefined);
						setState_showModal(false);
					}}
				/>
				{/* Like / Dislike buttons — gated by tour config `showLikeButtons` (default true) */}
				{tourState?.config?.showLikeButtons !== false && (
					<>
						<Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mb: 1 }}>
							{t('on_tour.stop.did_you_like')}
						</Typography>
						<Box className="flex justify-center items-start gap-6" sx={{ mb: 2.5 }}>
							<button
								onClick={() => handleStopReview('like')}
								style={STYLE_LIKE_BTN}
							>
								<i
									className={stopReview === 'like' ? 'fa-solid fa-thumbs-up' : 'fa-thin fa-thumbs-up'}
									style={{
										fontSize: 43,
										color: stopReview === 'like' ? '#FCCA47' : '#888',
										filter: stopReview === 'like' ? 'drop-shadow(0 0 2px rgba(252, 202, 71, 0.4))' : 'none',
										transition: 'color 0.2s, transform 0.2s, filter 0.2s',
										transform: stopReview === 'like' ? 'scale(1.15)' : 'scale(1)',
									}}
								/>
							</button>
							<button
								onClick={() => handleStopReview('dislike')}
								style={STYLE_DISLIKE_BTN}
							>
								<i
									className={stopReview === 'dislike' ? 'fa-solid fa-thumbs-down' : 'fa-thin fa-thumbs-down'}
									style={{
										fontSize: 43,
										color: stopReview === 'dislike' ? '#FCCA47' : '#888',
										filter: stopReview === 'dislike' ? 'drop-shadow(0 0 2px rgba(252, 202, 71, 0.4))' : 'none',
										transition: 'color 0.2s, transform 0.2s, filter 0.2s',
										transform: stopReview === 'dislike' ? 'scale(1.15)' : 'scale(1)',
									}}
								/>
							</button>
						</Box>
					</>
				)}

				<Box className="flex justify-center">
					<Button
						style={STYLE_FINISH_BTN}
						className="mb-5"
						onClick={() => {
							// Track stop as visited with full scroll depth (Finished = saw everything)
							if (state_activeStopState) {
								const geo = getGeolocation();
								maxScrollDepthRef.current = 100;
								passiveCollector.trackStopClosed(state_activeStopState.id, isWithinRadius(geo.latitude, geo.longitude, state_activeStopState as Stop), 'visited');
							}

							// Fire notification triggers for this stop
							if (state_activeStopState?.id) {
								fireTrigger('action', { stopId: state_activeStopState.id });
								fireTrigger('locationAndAction', { stopId: state_activeStopState.id });
							}

							// Clear stop_modal_state_ from localStorage to prevent reopening on refresh
							const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('stop_modal_state_'));
							keysToRemove.forEach(key => localStorage.removeItem(key));
							// Clear the saved stop state atom
							setSavedStopState(null);

							const storage = JSON.parse(localStorage.getItem('visitedStops') || '{}') as any
							const visitedStops = new Set<string>(Object.values(storage).length > 0 ? Object.values(storage) : undefined);
							if (!visitedStops.has((state_activeStopState as any)?.id)) {
								visitedStops.add((state_activeStopState as any)?.id);
								localStorage.setItem('visitedStops', JSON.stringify(({ ...Array.from(visitedStops) })));
							}
							if (state_activeStopState?.stop_index === getHighestStopIndex()) {
								setShowRatingModal(true);
							} else {
								setState_openedStopState(undefined);

							}
							setState_showModal(false);
							setState_modalType(null);
							setState_visitedLocations({
								...state_visitedLocations,
								...(state_activeStopState?.id && {
									[state_activeStopState?.id]: true
								})
							});
						}}
					>
						{t('on_tour.stop.i_finished_the_stop')}
					</Button>
				</Box>

			</Box >
			</>
		);
	}
);
